// apps/intranet/src/modules/bot/botMarkdown.tsx
//
// Tiny, SAFE renderer for the bot's own reply text (AnswerCard). Never
// touches dangerouslySetInnerHTML — every node is built by splitting plain
// strings into React elements. Covers exactly what the bot task layer
// (functions/intranet/src/lib/bot/thread.js) is instructed to use:
// **bold**, `code`, "- "/"· " bullet lists, and line breaks. Everything
// else — links, images, headings, an unmatched ** — is left as literal
// text, by construction (the regex below only ever matches complete pairs).

import type { ReactNode } from 'react'

const INLINE_PATTERN = /\*\*([^*]+)\*\*|`([^`]+)`/g

/**
 * Inline bold and code spans within one line. An unmatched ** or ` never
 * forms a span — the pattern only matches complete pairs, so a stray
 * delimiter simply stays in the surrounding plain-text slice.
 */
function renderInline(line: string, lineKey: number): ReactNode[] {
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let spanIndex = 0
  INLINE_PATTERN.lastIndex = 0
  let match = INLINE_PATTERN.exec(line)
  while (match !== null) {
    if (match.index > lastIndex) {
      nodes.push(line.slice(lastIndex, match.index))
    }
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${lineKey}-${spanIndex}`}>{match[1]}</strong>)
    } else if (match[2] !== undefined) {
      nodes.push(
        <code key={`${lineKey}-${spanIndex}`} className="font-mono text-[0.92em]">
          {match[2]}
        </code>,
      )
    }
    spanIndex += 1
    lastIndex = INLINE_PATTERN.lastIndex
    match = INLINE_PATTERN.exec(line)
  }
  if (lastIndex < line.length) {
    nodes.push(line.slice(lastIndex))
  }
  return nodes
}

function isBulletLine(line: string): boolean {
  return line.startsWith('- ') || line.startsWith('· ')
}

/**
 * Renders plain bot reply text as React elements. Supports **bold**,
 * `code`, preserved line breaks, and consecutive "- "/"· " lines collapsed
 * into a single <ul className="list-disc pl-4">. No links, no images, no
 * headings — plain text otherwise, verbatim.
 *
 * Keys are derived from each line's index in `text.split('\n')`. Callers
 * only ever append to `text` while streaming, so earlier indices never
 * change meaning across re-renders — keys stay stable per line index.
 */
export function renderBotText(text: string): ReactNode {
  const lines = text.split('\n')
  const nodes: ReactNode[] = []
  let listItems: ReactNode[] = []
  let listStart = -1

  function flushList() {
    if (listItems.length === 0) return
    nodes.push(
      <ul key={`list-${listStart}`} className="list-disc pl-4">
        {listItems}
      </ul>,
    )
    listItems = []
    listStart = -1
  }

  lines.forEach((line, index) => {
    if (isBulletLine(line)) {
      if (listStart === -1) listStart = index
      listItems.push(<li key={index}>{renderInline(line.slice(2), index)}</li>)
      return
    }
    flushList()
    if (index > 0) nodes.push(<br key={`br-${index}`} />)
    nodes.push(<span key={index}>{renderInline(line, index)}</span>)
  })
  flushList()

  return nodes
}
